import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, settingsAPI } from '../services/api';
import SEOHead from '../components/SEOHead';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // FIXED: For email changes, use pending_email, otherwise use email from params or user.email
  const email = user?.pending_email || searchParams.get('email') || user?.email;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // If user is already verified, redirect to dashboard
    if (user && user.is_verified) {
      navigate('/dashboard');
      return;
    }

    // If no email available, redirect to login
    if (!email) {
      navigate('/login');
      return;
    }

    // Start resend cooldown timer if active
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [user, email, navigate, resendCooldown]);

  const handleVerification = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Check if this is an email change verification (user has pending_email)
      if (user && user.email_verification_pending && user.pending_email === email) {
        // This is an email change verification
        const response = await settingsAPI.verifyEmailChange(email, verificationCode);
        
        // Update user with new email
        updateUser({ 
          email: email,
          pending_email: null,
          email_verification_pending: false,
          is_verified: true 
        });
        
        setSuccess('Email change verified successfully! Your new email is now active.');
        
        setTimeout(() => {
          navigate('/settings');
        }, 2000);
      } else {
        // Regular email verification for new users
        await authAPI.verifyEmail(email, verificationCode);
        
        // If user is logged in, update their verification status
        if (user && user.email === email) {
          updateUser({ is_verified: true });
          
          setSuccess('Email verified successfully! Redirecting to dashboard...');
          
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          // If not logged in, redirect to login
          setSuccess('Email verified successfully! Please log in to continue.');
          
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: 'Email verified successfully! You can now log in.',
                type: 'success'
              }
            });
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Email verification error:', error);
      
      // Soften error messages for better user experience
      let errorMessage = error.response?.data?.detail || 'Verification failed';
      
      // Make error messages more user-friendly
      if (errorMessage.includes('Invalid or expired') || errorMessage.includes('incorrect or has expired')) {
        errorMessage = "That code doesn't look right. Please check your code or request a new one.";
      } else if (errorMessage.includes('User not found') || errorMessage.includes("couldn't find an account")) {
        errorMessage = "There seems to be an issue with your account. Please contact support if this continues.";
      } else if (errorMessage.includes('temporary issue')) {
        errorMessage = "We're having a temporary issue. Please try again in a few moments.";
      } else if (!errorMessage.includes('Please') && !errorMessage.includes('try again')) {
        // Generic fallback for any other harsh messages
        errorMessage = "Verification failed. Please check your code and try again.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError('');
    
    try {
      await authAPI.resendVerification(email);
      setSuccess('Verification code sent! Please check your email.');
      setResendCooldown(60); // 60 second cooldown
      
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      // Soften error messages for better user experience
      let errorMessage = error.response?.data?.detail || 'Unable to send verification code';
      
      // Make error messages more user-friendly
      if (errorMessage.includes('User not found') || errorMessage.includes("couldn't find an account")) {
        errorMessage = "Please check your email address and try again.";
      } else if (errorMessage.includes('already verified')) {
        errorMessage = "Your email is already verified! You can proceed to log in.";
      } else if (errorMessage.includes('Failed to send') || errorMessage.includes('temporary issue')) {
        errorMessage = "Having trouble sending the code. Please wait a moment and try again.";
      }
      
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead 
        title="Email Verification" 
        description="Verify your email address"
        isIndexable={false}
      />
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-24 w-24 bg-white rounded-full flex items-center justify-center mb-6">
            <span className="text-4xl">📧</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Verify Your Email
          </h2>
          <p className="text-purple-200">
            We've sent a 6-digit verification code to
          </p>
          <p className="text-white font-semibold text-lg mt-1">
            {email}
          </p>
        </div>

        {/* Verification Form */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          <form onSubmit={handleVerification} className="space-y-6">
            {/* Verification Code Input */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Enter 6-digit verification code
              </label>
              <input
                id="code"
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-2xl font-bold tracking-widest text-gray-900 bg-white"
                placeholder="000000"
                maxLength="6"
                required
              />
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <span className="text-red-500 mr-2">⚠️</span>
                  {error}
                </div>
              </div>
            )}

            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✅</span>
                  {success}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </div>
              ) : (
                'Verify Email'
              )}
            </button>
          </form>

          {/* Resend Code Section */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 mb-4">
              Didn't receive the code?
            </p>
            
            <button
              onClick={handleResendCode}
              disabled={resendLoading || resendCooldown > 0}
              className="text-purple-600 font-semibold hover:text-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {resendLoading ? (
                'Sending...'
              ) : resendCooldown > 0 ? (
                `Resend in ${resendCooldown}s`
              ) : (
                'Resend Code'
              )}
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>The verification code will expire in 30 minutes.</p>
            <p className="mt-2">
              Need help? Contact our{' '}
              <a href="/contact" className="text-purple-600 hover:text-purple-800">
                support team
              </a>
            </p>
          </div>
        </div>

        {/* Back to Login Link */}
        <div className="text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-purple-200 hover:text-white transition duration-200"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;